import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";

const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0
});

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    processingOrders: 0,
    deliveredOrders: 0
  });

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | PROCESSING | CONFIRMED | SHIPPED | DELIVERED | CANCELLED
  const [dateRange, setDateRange] = useState("ALL"); // ALL | TODAY | THIS_WEEK | THIS_MONTH
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // Order Details Modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const [feedback, setFeedback] = useState({ type: "", msg: "" });

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/vendor/orders?status=${statusFilter}&dateRange=${dateRange}&search=${encodeURIComponent(appliedSearch)}`);
      if (res.data) {
        setOrders(res.data.orders || []);
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      }
    } catch (err) {
      setFeedback({ type: "error", msg: err.response?.data?.msg || "Failed to load orders." });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateRange, appliedSearch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (appliedSearch === searchTerm) {
      fetchOrders();
    } else {
      setAppliedSearch(searchTerm);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setActionLoading(true);
      const res = await api.put(`/vendor/orders/${orderId}/status`, { orderStatus: newStatus });
      setFeedback({ type: "success", msg: res.data?.message || `Order status updated to ${newStatus}.` });
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder({ ...selectedOrder, orderStatus: newStatus });
      }
      fetchOrders();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to update order status.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", paddingBottom: "40px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "900", color: "#0f172a", margin: "0 0 4px" }}>
            🛍️ Orders Management
          </h1>
          <p style={{ fontSize: "14px", color: "var(--text-muted)", margin: 0 }}>
            Track and process customer orders, view line item details, and manage fulfillment lifecycles.
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <Link
            to="/vendor/pickup-requests"
            className="btn btn-primary"
            style={{ padding: "9px 18px", fontSize: "13px", fontWeight: "800", gap: "6px", display: "inline-flex", alignItems: "center" }}
          >
            <span>🚚 Go to Pickup &amp; Fulfillment</span>
            <span>&rarr;</span>
          </Link>
        </div>
      </div>

      {/* Feedback Banner */}
      {feedback.msg && (
        <div
          style={{
            background: feedback.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: `1.5px solid ${feedback.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            color: feedback.type === "success" ? "#166534" : "#991b1b",
            padding: "12px 18px",
            borderRadius: "10px",
            marginBottom: "18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "13.5px",
            fontWeight: "700"
          }}
        >
          <span>{feedback.msg}</span>
          <button type="button" onClick={() => setFeedback({ type: "", msg: "" })} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px" }}>✕</button>
        </div>
      )}

      {/* Top Summary Metrics */}
      <div className="row g-3 mb-4">
        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Total Orders</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>
              {stats.totalOrders}
            </div>
            <span style={{ fontSize: "11.5px", color: "#64748b" }}>Lifetime store orders</span>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#166534", textTransform: "uppercase" }}>Store Sales</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#16a34a", marginTop: "4px" }}>
              {inrFormatter.format(stats.totalRevenue)}
            </div>
            <span style={{ fontSize: "11.5px", color: "#166534" }}>Gross orders volume</span>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1.5px solid #fef3c7", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#b45309", textTransform: "uppercase" }}>⏳ Processing / Pending</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#b45309", marginTop: "4px" }}>
              {stats.processingOrders}
            </div>
            <span style={{ fontSize: "11.5px", color: "#b45309" }}>Requires packing / action</span>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1.5px solid #dcfce7", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#166534", textTransform: "uppercase" }}>✓ Delivered Orders</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#16a34a", marginTop: "4px" }}>
              {stats.deliveredOrders}
            </div>
            <span style={{ fontSize: "11.5px", color: "#166534" }}>Successfully completed</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "18px 22px", marginBottom: "22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "14px" }}>
        {/* Status Tabs */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {[
            { id: "ALL", label: "All Orders" },
            { id: "PROCESSING", label: "⏳ Processing" },
            { id: "CONFIRMED", label: "Confirmed" },
            { id: "SHIPPED", label: "🛵 Shipped" },
            { id: "DELIVERED", label: "✓ Delivered" },
            { id: "CANCELLED", label: "✕ Cancelled" }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              style={{
                padding: "6px 14px",
                fontSize: "12.5px",
                fontWeight: "800",
                borderRadius: "8px",
                border: "none",
                background: statusFilter === tab.id ? "var(--primary)" : "#f1f5f9",
                color: statusFilter === tab.id ? "#ffffff" : "#475569",
                cursor: "pointer"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search & Date Controls */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px", fontWeight: "700", color: "#334155" }}
          >
            <option value="ALL">All Time</option>
            <option value="TODAY">Today</option>
            <option value="THIS_WEEK">Last 7 Days</option>
            <option value="THIS_MONTH">Last 30 Days</option>
          </select>

          <form onSubmit={handleSearchSubmit} style={{ display: "flex", gap: "6px" }}>
            <input
              type="text"
              placeholder="Search Order #, Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px", width: "200px" }}
            />
            <button type="submit" className="btn btn-outline-secondary" style={{ padding: "8px 14px", fontSize: "13px", fontWeight: "700" }}>
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Orders Table */}
      <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
            <div className="spinner-border text-brand-primary" role="status" />
            <p style={{ marginTop: "10px", fontSize: "13.5px" }}>Loading orders...</p>
          </div>
        ) : orders.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid #edf0f4", color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>ORDER #</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>DATE</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>CUSTOMER</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>ITEMS</th>
                  <th style={{ padding: "10px 8px", textAlign: "right" }}>ORDER AMOUNT</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>PAYMENT</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>STATUS</th>
                  <th style={{ padding: "10px 8px", textAlign: "right" }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((ord) => (
                  <tr key={ord._id} style={{ borderBottom: "1px solid #edf0f4" }}>
                    <td style={{ padding: "14px 8px" }}>
                      <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>#{ord.orderNumber}</strong>
                      <span style={{ display: "block", fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>
                        {ord.fulfillmentMethod === "CUSTOMER_PICKUP" ? "🏪 Self Pickup" : ord.fulfillmentMethod === "VENDOR_DELIVERY" ? "🛵 Vendor Direct" : "📦 Home Delivery"}
                      </span>
                    </td>
                    <td style={{ padding: "14px 8px", color: "#475569" }}>
                      <div>{new Date(ord.createdAt).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</div>
                      <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                        {new Date(ord.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td style={{ padding: "14px 8px" }}>
                      <strong style={{ color: "#0f172a", display: "block" }}>{ord.customer?.name}</strong>
                      <span style={{ fontSize: "11.5px", color: "#64748b" }}>{ord.customer?.phone}</span>
                    </td>
                    <td style={{ padding: "14px 8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {ord.items?.[0]?.image && (
                          <img
                            src={ord.items[0].image}
                            alt=""
                            style={{ width: "36px", height: "36px", borderRadius: "6px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                          />
                        )}
                        <div>
                          <strong style={{ fontSize: "12.5px", color: "#1e293b", display: "block" }}>
                            {ord.items?.[0]?.name?.slice(0, 24)}...
                          </strong>
                          <span style={{ fontSize: "11px", color: "#64748b" }}>
                            Qty: {ord.itemsCount} {ord.items?.length > 1 ? `(+${ord.items.length - 1} more)` : ""}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "14px 8px", textAlign: "right", fontWeight: "800", color: "#0f172a", fontSize: "13.5px" }}>
                      {inrFormatter.format(ord.vendorTotal || 0)}
                    </td>
                    <td style={{ padding: "14px 8px" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: "800",
                          padding: "2px 8px",
                          borderRadius: "99px",
                          background: ord.paymentStatus === "Paid" ? "#dcfce7" : "#fef3c7",
                          color: ord.paymentStatus === "Paid" ? "#166534" : "#92400e"
                        }}
                      >
                        {ord.paymentStatus}
                      </span>
                      <span style={{ display: "block", fontSize: "10.5px", color: "#64748b", marginTop: "2px" }}>
                        {ord.paymentMode}
                      </span>
                    </td>
                    <td style={{ padding: "14px 8px" }}>
                      <span
                        style={{
                          fontSize: "11.5px",
                          fontWeight: "800",
                          padding: "3px 10px",
                          borderRadius: "99px",
                          background:
                            ord.orderStatus === "Delivered"
                              ? "#dcfce7"
                              : ord.orderStatus === "Confirmed"
                              ? "#e0e7ff"
                              : ord.orderStatus === "Cancelled"
                              ? "#fee2e2"
                              : "#fef3c7",
                          color:
                            ord.orderStatus === "Delivered"
                              ? "#166534"
                              : ord.orderStatus === "Confirmed"
                              ? "#3730a3"
                              : ord.orderStatus === "Cancelled"
                              ? "#991b1b"
                              : "#92400e"
                        }}
                      >
                        ● {ord.orderStatus}
                      </span>
                    </td>
                    <td style={{ padding: "14px 8px", textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedOrder(ord);
                          setDetailsModalOpen(true);
                        }}
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: "12px", fontWeight: "800", padding: "5px 12px" }}
                      >
                        Details &rarr;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "50px 20px", textAlign: "center", color: "var(--text-muted)" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>📦</div>
            <h3 style={{ fontSize: "16px", fontWeight: "800", color: "#0f172a", margin: "0 0 4px" }}>No Orders Found</h3>
            <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
              There are no orders matching your current filter. When customers buy your products, they will appear here.
            </p>
          </div>
        )}
      </div>

      {/* ═══════════════════════ ORDER DETAILS MODAL ═══════════════════════ */}
      {detailsModalOpen && selectedOrder && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 9999, padding: "20px", overflowY: "auto" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", padding: "26px", maxWidth: "650px", width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", maxHeight: "90vh", overflowY: "auto" }}>
            {/* Modal Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h3 style={{ fontSize: "19px", fontWeight: "900", color: "#0f172a", margin: "0 0 2px" }}>
                  Order Details #{selectedOrder.orderNumber}
                </h3>
                <span style={{ fontSize: "12px", color: "#64748b" }}>
                  Placed on {new Date(selectedOrder.createdAt).toLocaleString("en-IN")}
                </span>
              </div>
              <button type="button" onClick={() => setDetailsModalOpen(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>✕</button>
            </div>

            {/* Customer & Delivery Summary Card */}
            <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px", marginBottom: "16px", border: "1px solid #edf0f4", fontSize: "13px" }}>
              <div className="row g-2">
                <div className="col-md-6">
                  <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Customer Contact</span>
                  <strong style={{ display: "block", color: "#0f172a", marginTop: "2px" }}>{selectedOrder.customer?.name}</strong>
                  <div style={{ color: "#475569" }}>📞 {selectedOrder.customer?.phone}</div>
                  {selectedOrder.customer?.email && <div style={{ color: "#64748b", fontSize: "12px" }}>✉️ {selectedOrder.customer.email}</div>}
                </div>
                <div className="col-md-6">
                  <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Shipping / Collection Address</span>
                  <div style={{ color: "#0f172a", marginTop: "2px", fontWeight: "600" }}>
                    {selectedOrder.customer?.address || "Storefront Collection"}
                  </div>
                  <div style={{ color: "#64748b", fontSize: "12px" }}>
                    {selectedOrder.customer?.city ? `${selectedOrder.customer.city}, ${selectedOrder.customer.state || ''} - ${selectedOrder.customer.pincode}` : ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items List */}
            <h4 style={{ fontSize: "14px", fontWeight: "900", color: "#0f172a", marginBottom: "10px" }}>Ordered Items</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "18px" }}>
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#ffffff", border: "1px solid #edf0f4", borderRadius: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {item.image && (
                      <img src={item.image} alt="" style={{ width: "44px", height: "44px", borderRadius: "6px", objectFit: "cover", border: "1px solid #e2e8f0" }} />
                    )}
                    <div>
                      <strong style={{ fontSize: "13px", color: "#0f172a", display: "block" }}>{item.name}</strong>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>
                        Size: {item.size || "Standard"} &bull; Color: {item.colour || "Standard"} &bull; Qty: {item.quantity || 1}
                      </span>
                    </div>
                  </div>
                  <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>
                    {inrFormatter.format((item.price || 0) * (item.quantity || 1))}
                  </strong>
                </div>
              ))}
            </div>

            {/* Quick Status Updater */}
            <div style={{ background: "#f8fafc", padding: "14px 16px", borderRadius: "10px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
              <div>
                <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Current Order Status:</span>
                <strong style={{ fontSize: "14px", color: "var(--primary)", display: "block" }}>{selectedOrder.orderStatus}</strong>
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                {selectedOrder.orderStatus !== "Confirmed" && selectedOrder.orderStatus !== "Delivered" && (
                  <button
                    type="button"
                    onClick={() => handleUpdateStatus(selectedOrder._id, "Confirmed")}
                    disabled={actionLoading}
                    className="btn btn-sm btn-outline-primary"
                    style={{ fontSize: "12px", fontWeight: "800" }}
                  >
                    Mark Confirmed
                  </button>
                )}
                {selectedOrder.orderStatus !== "Delivered" && (
                  <button
                    type="button"
                    onClick={() => handleUpdateStatus(selectedOrder._id, "Delivered")}
                    disabled={actionLoading}
                    className="btn btn-sm btn-success"
                    style={{ fontSize: "12px", fontWeight: "800" }}
                  >
                    ✓ Mark Delivered
                  </button>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Link
                to="/vendor/pickup-requests"
                className="btn btn-outline-primary"
                style={{ fontSize: "12.5px", fontWeight: "800" }}
              >
                🚚 Request Courier / Pickup &rarr;
              </Link>

              <button
                type="button"
                onClick={() => setDetailsModalOpen(false)}
                className="btn btn-secondary"
                style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "800" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
